/**
 * Login Form Card - Premium glassmorphism form
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useCallback, useState } from 'react';
import { Control, Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion, useReducedMotion } from 'framer-motion';

import { useAuth } from '@/hooks';
import { useToast } from '@/providers/ToastProvider';
import { Button } from '@/components/ui';
import { Input } from '@/components/ui';
import { Checkbox } from '@/components/ui';
import { cn } from '@lib/utils';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional(),
});

type LoginForm = z.infer<typeof loginSchema>;

interface RememberMeCheckboxProps {
  value: boolean | undefined;
  onChange: (value: boolean) => void;
}

const RememberMeCheckbox: FC<RememberMeCheckboxProps> = ({ value = false, onChange }) => (
  <label
    htmlFor="rememberMe"
    className="flex cursor-pointer items-center gap-2.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
  >
    <Checkbox id="rememberMe" checked={value} onCheckedChange={onChange} />
    <span>Remember me for 30 days</span>
  </label>
);

const RememberMeField: FC<{ control: Control<LoginForm> }> = ({ control }) => (
  <Controller
    name="rememberMe"
    control={control}
    render={({ field }) => <RememberMeCheckbox value={field.value} onChange={field.onChange} />}
  />
);

/** Premium login form with email, password, and remember-me. */
export const LoginFormCard: FC<{ className?: string }> = ({ className }) => {
  const noMotion = !!useReducedMotion();
  const { login } = useAuth();
  const { error: showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { rememberMe: false },
  });

  const onSubmit = useCallback(
    async (data: LoginForm) => {
      setIsLoading(true);
      try {
        await login(data.email, data.password, data.rememberMe);
      } catch {
        showError('Login failed', 'Invalid email or password');
      } finally {
        setIsLoading(false);
      }
    },
    [login, showError],
  );

  const stagger = noMotion ? 0 : 0.07;
  const itemVariants = {
    hidden: { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: noMotion ? 0 : 0.35 } },
  };

  return (
    <motion.div
      initial={noMotion ? false : { opacity: 0, y: 24, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: noMotion ? 0 : 0.6,
        delay: noMotion ? 0 : 0.18,
        ease: [0.22, 1, 0.36, 1],
      }}
      className={cn('order-1 flex min-h-0 items-center lg:order-2 lg:w-full lg:max-w-md', className)}
    >
      <div
        className={cn(
          'rounded-2xl border border-border/60 bg-card/98 p-7 shadow-2xl',
          'backdrop-blur-xl ring-1 ring-white/20 dark:ring-white/5 sm:p-9',
        )}
      >
        <div className="mb-7">
          <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            Sign in to your account
          </h2>
          <p className="mt-2.5 text-base text-muted-foreground">
            Enter your credentials to access your dashboard
          </p>
        </div>
        <motion.form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-5"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: {
              transition: { staggerChildren: stagger, delayChildren: 0.08 },
            },
          }}
        >
          <motion.div variants={itemVariants}>
            <Input
              type="email"
              placeholder="Email address"
              className="h-12 text-base focus-visible:ring-2 focus-visible:ring-primary/30"
              {...register('email')}
              error={errors.email?.message}
            />
          </motion.div>
          <motion.div variants={itemVariants}>
            <Input
              type="password"
              placeholder="Password"
              className="h-12 text-base focus-visible:ring-2 focus-visible:ring-primary/30"
              {...register('password')}
              error={errors.password?.message}
            />
          </motion.div>
          <motion.div variants={itemVariants}>
            <RememberMeField control={control} />
          </motion.div>
          <motion.div variants={itemVariants}>
            <Button
              type="submit"
              className="h-12 w-full text-base font-semibold"
              loading={isLoading}
            >
              Sign In
            </Button>
          </motion.div>
        </motion.form>
      </div>
    </motion.div>
  );
};
